import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { MUSIC24_TRIAL_DAYS, MUSIC24_TRIAL_LEVEL_TIER, Music24Capabilities } from 'fym-common/src/license/music24Plans';
import { TypedMemo } from '../../common/types';
import { LoadingIcon } from '../../common/views/LoadingIcon';
import { Panel } from '../../music24/components/primitives/Panel';
import { planNameMessages, planDescriptionMessages } from '../../music24/config/music24PlanNames';
import styles from './SubscriptionPlanCard.module.scss';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';

// The native apps read this id with the date only (scripts/music24-native/i18n-keys.txt). The web
// uses `app.sml.settings.subscription.trialDescriptionPlan`. Keep it until the apps stop.
defineMessages({
  nativeTrialDescription: {
    id: 'app.sml.settings.subscription.trialDescription',
    defaultMessage: 'You have 7 days of Pro, until {date}. Then you move to Free. Nothing is charged.',
  },
});

interface SubscriptionPlanCardProps {
  capabilities: Music24Capabilities;
  isActive: boolean;
  trialEndsAt: Date | null;
  /** `licenseInfo.trialDaysRemaining` — the same count the trial pill reads, so the two
   * never disagree on how many days are left. */
  trialDaysRemaining: number | null;
  /** `licenseInfo.trialLimitsUntil`: a plan bought during the trial keeps the trial limits until
   * this date. */
  trialLimitsUntil?: Date | null;
  renewalDate: Date | undefined;
  portalUrl: string | null;
  isPortalLoading: boolean;
  onOpenPricing: () => void;
}

/**
 * Design r1–r3's plan card: the plan name, a status chip, one line about the dates, and the
 * card's two actions. "View plans" always opens the plan picker (a); "Manage Subscription" opens
 * the Stripe customer portal. It is a disabled loading button while the portal URL loads, and it
 * goes away when no URL comes back (a native app).
 */
const SubscriptionPlanCardComponent: React.FC<SubscriptionPlanCardProps> = ({
  capabilities,
  isActive,
  trialEndsAt,
  trialDaysRemaining,
  trialLimitsUntil = null,
  renewalDate,
  portalUrl,
  isPortalLoading,
  onOpenPricing,
}) => {
  const intl = useIntl();
  const planName = intl.formatMessage(planNameMessages[capabilities.plan]);
  const isTrial = capabilities.plan === 'trial';

  const statusText = isTrial
    ? trialDaysRemaining === null
      ? intl.formatMessage({ id: 'app.sml.settings.subscription.status.trialActive', defaultMessage: 'Trial active' })
      : intl.formatMessage(
          {
            id: 'app.sml.settings.subscription.status.trial',
            defaultMessage: 'Trial · {days, plural, one {# day left} other {# days left}}',
          },
          { days: trialDaysRemaining }
        )
    : isActive
      ? intl.formatMessage({ id: 'app.sml.settings.subscription.status.active', defaultMessage: 'Active Subscription' })
      : intl.formatMessage({
          id: 'app.sml.settings.subscription.status.inactive',
          defaultMessage: 'No active subscription',
        });

  const description = isTrial
    ? intl.formatMessage(
        {
          id: 'app.sml.settings.subscription.trialDescriptionPlan',
          defaultMessage: 'You have {days} days of {plan}, until {date}. Then you move to Free. Nothing is charged.',
        },
        {
          days: MUSIC24_TRIAL_DAYS,
          plan: intl.formatMessage(planNameMessages[MUSIC24_TRIAL_LEVEL_TIER]),
          date:
            trialEndsAt === null
              ? ''
              : intl.formatDate(trialEndsAt, { year: 'numeric', month: 'short', day: 'numeric' }),
        }
      )
    : intl.formatMessage(planDescriptionMessages[capabilities.plan === 'trial' ? 'free' : capabilities.plan]);

  const hasManage = isPortalLoading || portalUrl !== null;
  const manageLabel = intl.formatMessage({
    id: 'app.sml.settings.subscription.manage',
    defaultMessage: 'Manage Subscription',
  });

  return (
    <Panel pad className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
      <div>
        <div className={styles.name}>
          <span data-testid="subscription-plan-name">{planName}</span>
          <span
            className={clsx(styles.status, isTrial && styles.statusTrial, !isTrial && isActive && styles.statusActive)}
          >
            {statusText}
          </span>
        </div>
        <p className="text-muted mb-0 mt-1">{withBrandWord(description)}</p>
        {!isTrial && trialLimitsUntil !== null && (
          <p className="text-muted mb-0" data-testid="subscription-trial-limits">
            {intl.formatMessage(
              {
                id: 'app.sml.settings.subscription.trialLimitsUntil',
                defaultMessage: '{trialPlan} limits until {date}, then {plan}.',
              },
              {
                trialPlan: intl.formatMessage(planNameMessages[MUSIC24_TRIAL_LEVEL_TIER]),
                date: intl.formatDate(trialLimitsUntil, { year: 'numeric', month: 'short', day: 'numeric' }),
                plan: planName,
              }
            )}
          </p>
        )}
        {isActive && renewalDate !== undefined && (
          <p className="text-muted mb-0">
            {intl.formatMessage(
              { id: 'app.sml.settings.subscription.accessUntil', defaultMessage: 'Current access until {date}' },
              { date: intl.formatDate(renewalDate, { year: 'numeric', month: 'short', day: 'numeric' }) }
            )}
          </p>
        )}
      </div>
      <div className="d-flex flex-wrap gap-2">
        {/* "View plans" is an owner's action regardless of whether the portal URL has loaded yet —
         * it must never disappear while `isPortalLoading` is true. Only "Manage Subscription"
         * depends on that portal URL. */}
        <Button color={hasManage ? 'secondary' : 'primary'} outline={hasManage} onClick={onOpenPricing}>
          {intl.formatMessage({ id: 'app.sml.settings.subscription.upgrade', defaultMessage: 'View plans' })}
        </Button>
        {/* While the portal URL loads, "Manage Subscription" already sits in its place as a
         * disabled button with a spinner, so the card does not change its layout when the URL arrives. */}
        {isPortalLoading ? (
          <Button color="primary" disabled aria-busy="true">
            <span className="me-2">
              <LoadingIcon small />
            </span>
            {manageLabel}
          </Button>
        ) : (
          portalUrl !== null && (
            <Button tag="a" href={portalUrl} target="_blank" rel="noopener noreferrer" color="primary">
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="me-2" />
              {manageLabel}
            </Button>
          )
        )}
      </div>
    </Panel>
  );
};

export const SubscriptionPlanCard = TypedMemo(SubscriptionPlanCardComponent);
